import { FileHelper } from '@/helpers/file.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  PhotosService,
  PropertiesService,
  PropertyType,
  RealProperty,
  RealPropertySummaryLocation,
  SellingStatus,
  UsersService
} from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BadgeSize, CertificationBadgeComponent } from '../certification-badge/certification-badge.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-property-card',
  templateUrl: './property-card.component.html',
  styleUrls: ['./property-card.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    CertificationBadgeComponent,
    NgIf,
    MatIconModule,
    NgFor,
    CurrencyPipe,
    TranslateModule,
    TooltipDirective,
    MatProgressSpinnerModule
  ]
})
export class PropertyCardComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() propSummary: RealPropertySummaryLocation;
  @Input() isFavorite = false;
  @Input() showMapLink = false;
  @Input() showFavorite = false;
  @Input() showOwnerName = false;
  @Input() showAddress = true;
  @Input() smallCard = false;
  @Output() favoriteToogled: EventEmitter<boolean> = new EventEmitter<boolean>();

  subs: Subscription[] = [];

  mainPhoto: SafeUrl;
  certifBadgeSize;
  routerLink: string[];

  propName: string;
  ownerName: string;
  displayAddress: string[];
  isOnSelling: boolean;
  sellingStatus: SellingStatus;
  price = 0;
  condoImageName: string;
  badgeImageName: string;

  constructor(
    private propertyService: PropertiesService,
    private photoService: PhotosService,
    private userService: UsersService,
    public propertyHelper: PropertyHelper,
    public userHelper: UserHelper,
    private domSanitizer: DomSanitizer,
    private deviceService: DeviceDetectorService,
    private filerHelper: FileHelper
  ) {}

  ngOnInit(): void {
    if (this.propSummary && !this.prop) {
      // a prop summary location has been provided, get the full prop
      this.subs.push(
        this.propertyService.getRealProperty(this.propSummary.uid).subscribe((p) => {
          this.prop = p;
          this.loadData();
        })
      );
    } else {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadData() {
    this.propName = this.prop.name;
    // the owner name is the one who shared if shared
    this.ownerName =
      this.prop.sharedBy && !this.prop.demo
        ? this.propertyHelper.getSharedUserNameShort(this.prop)
        : this.propertyHelper.getOwnersNameShort(this.prop);
    this.certifBadgeSize = this.smallCard ? BadgeSize.SMALL : BadgeSize.NORMAL;
    // this.mainPhoto = this.propertyHelper.getEmptyMainPhotoImage();
    this.subs.push(
      this.photoService.getMainPhotoImage(this.prop.uid, '134x134', this.prop.mainPhotoTs).subscribe((photo) => {
        this.mainPhoto = this.filerHelper.imageApiToHtml(photo);
      })
    );
    this.routerLink = this.propertyHelper.getPropRouterLink(this.prop);
    this.isOnSelling =
      !this.prop.demo &&
      this.prop.onGoingSelling != null &&
      this.prop.onGoingSelling?.status !== SellingStatus.CANCELED &&
      this.prop.onGoingSelling?.status !== SellingStatus.SOLD;
    if (this.isOnSelling) {
      this.sellingStatus = [SellingStatus.SELLING, SellingStatus.UNDER_OFFER, SellingStatus.UNDER_AGREEMENT].includes(
        this.prop.onGoingSelling.status
      )
        ? this.prop.onGoingSelling.status
        : SellingStatus.UNDER_AGREEMENT;
    }
    this.price = this.prop.onGoingSelling?.price ? this.prop.onGoingSelling.price : 0;
    this.condoImageName = this.propertyHelper.getBadgeImageName(PropertyType.CONDOMINIUM);
    this.badgeImageName = this.propertyHelper.getBadgeImageName(this.prop.type);

    // if ongoing selling and viewing mode we show only the city
    if (this.isOnSelling && !this.userHelper.canUpdateProperty(this.prop)) {
      this.displayAddress = [this.propertyHelper.getAddressCityName(this.prop.address)];
    } else {
      this.displayAddress = this.propertyHelper.getAddressLines(this.prop.address);
    }
  }

  toogleFavorite() {
    this.subs.push(
      this.userService
        .toggleUserPreferencesFavorite(this.userHelper.currentUser.userId, this.prop.uid)
        .subscribe(() => this.favoriteToogled.emit(true))
    );
  }

  get externalMapLink(): SafeUrl {
    const coord = this.prop.location.latitude + ',' + this.prop.location.longitude;
    let proto = 'https';
    if (
      (this.deviceService.isMobile() || this.deviceService.isTablet()) &&
      this.deviceService.os.indexOf('iOS') !== -1
    ) {
      proto = 'maps';
    }
    const link = proto + '://maps.google.com/maps?daddr=' + coord + '&amp;ll=';
    return this.domSanitizer.bypassSecurityTrustUrl(link);
  }
}
